// src/app/components/settings/institucion/institucion.component.ts
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

import {
  InstitutionSettings,
  InstitutionSettingsService,
  UpdateInstitutionSettingsDto,
} from 'src/app/core/services/institution-settings.service';
import { BrandingService } from 'src/app/core/services/branding.service';
import { environment } from 'src/environments/environment'; // 👈 NUEVO

@Component({
  selector: 'app-institucion',
  templateUrl: './institucion.component.html',
  styleUrls: ['./institucion.component.scss'],
})
export class InstitucionComponent implements OnInit {
  form!: FormGroup;
  loading = false;
  loadingLogo = false;

  settings: InstitutionSettings | null = null;
  previewLogoUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private institutionSettingsService: InstitutionSettingsService,
    private brandingService: BrandingService,
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadSettings();
  }

  private buildForm() {
    this.form = this.fb.group({
      // 👇 nombres alineados con el backend y con UpdateInstitutionSettingsDto
      name: ['', [Validators.required, Validators.maxLength(100)]],
      primaryColor: ['#1976d2', [Validators.required]],
      emailHeader: [null],
      emailBody: [null],
      emailFooter: [null],
      logoUrl: [null], // solo informativo para mostrar en el form
    });
  }

  private async loadSettings(): Promise<void> {
    
    try {
      this.settings = await lastValueFrom(
        this.institutionSettingsService.getSettings(false),
      );

      if (!this.settings) {
        return;
      }

      this.form.patchValue({
        name: this.settings.name,
        primaryColor: this.settings.primaryColor || '#1976d2',
        emailHeader: this.settings.emailHeader,
        emailBody: this.settings.emailBody,
        emailFooter: this.settings.emailFooter,
        logoUrl: this.settings.logoUrl,
      });

      this.previewLogoUrl = this.buildLogoUrl(this.settings);

    } catch (error) {
      console.error('Error cargando configuración:', error);

      Swal.fire(
        'Error',
        'No se pudo cargar la configuración institucional.',
        'error',
      );
    }
  }

  // 👇 Helper local para construir la URL pública del logo
  private buildLogoUrl(settings: InstitutionSettings | null): string | null {
    if (!settings?.logoUrl) return null;

    const raw = settings.logoUrl.trim();
    // Soporta tanto "logo-123.png" como "/api/v1/assets/branding/logo-123.png"
    const fileNameWithExt = raw.includes('/') ? raw.split('/').pop()! : raw;

    const parts = fileNameWithExt.split('.');
    if (parts.length < 2) return null;

    const ext = parts.pop()!.toLowerCase();   // "png"
    const baseName = parts.join('.');        // "logo-123"

    const allowedExt = ['png', 'jpg', 'jpeg', 'ico'];
    if (!allowedExt.includes(ext)) return null;

    const apiBase = environment.base.replace(/\/+$/, '');

    return `${apiBase}/assets/branding/${baseName}?ext=${ext}`;
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    try {
      const dto: UpdateInstitutionSettingsDto = {
        name: this.form.value.name,
        primaryColor: this.form.value.primaryColor,
        emailHeader: this.form.value.emailHeader,
        emailBody: this.form.value.emailBody,
        emailFooter: this.form.value.emailFooter,
      };

      const updated = await lastValueFrom(
        this.institutionSettingsService.updateSettings(dto),
      );

      this.settings = updated;
      this.form.patchValue({
        logoUrl: updated.logoUrl,
      });

      // (Opcional) actualizar también la preview por si algún día el logo viniera en este update
      this.previewLogoUrl = this.buildLogoUrl(updated);

      // refrescar branding global (header, sidebar, login, CSS vars)
      await this.brandingService.load();

      Swal.fire(
        'Guardado',
        'La configuración institucional se actualizó correctamente.',
        'success',
      );
    } catch (error: any) {

      Swal.fire(
        'Error',
        error?.error?.message || 'No se pudo actualizar la configuración.',
        'error',
      );
    } finally {
      this.loading = false;
    }
  }

  async onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire('Archivo inválido', 'El logo debe ser una imagen.', 'warning');
      input.value = '';
      return;
    }

    this.loadingLogo = true;
    try {
      const updated = await lastValueFrom(
        this.institutionSettingsService.uploadLogo(file),
      );

      this.settings = updated;
      this.form.patchValue({
        logoUrl: updated.logoUrl,
      });

      // 👇 ahora preview usa el endpoint nuevo
      this.previewLogoUrl = this.buildLogoUrl(updated);

      await this.brandingService.load();

      Swal.fire(
        'Logo actualizado',
        'El logo institucional se actualizó correctamente.',
        'success',
      );
    } catch (error: any) {

      Swal.fire(
        'Error',
        error?.error?.message || 'No se pudo subir el logo.',
        'error',
      );
    } finally {
      this.loadingLogo = false;
      input.value = '';
    }
  }
}
